import type { SalesOrderLineItem } from '@erp/rpc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateOnlyString } from '@/utils/dateUtils';

// Helper function to format currency
const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return '-';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

interface SalesOrderLineItemsProps {
  lineItems: SalesOrderLineItem[];
  currency?: string | null;
}

export function SalesOrderLineItems({
  lineItems,
  currency,
}: SalesOrderLineItemsProps) {
  if (lineItems.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        No line items found
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[60px]">#</TableHead>
          <TableHead>Product</TableHead>
          <TableHead className="text-right">Qty</TableHead>
          <TableHead className="text-right">Unit Price</TableHead>
          <TableHead className="text-right">Total</TableHead>
          <TableHead>Delivery</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lineItems.map((item, index) => (
          <TableRow key={item.id}>
            <TableCell className="font-mono text-muted-foreground">
              {item.lineItemNumber ?? index + 1}
            </TableCell>
            <TableCell>
              <div className="flex flex-col">
                <span className="font-medium">
                  {item.productName || 'Unknown Product'}
                </span>
                {(item.productSku || item.customerProductSku) && (
                  <span className="text-xs text-muted-foreground">
                    {item.customerProductSku && (
                      <span>Customer SKU: {item.customerProductSku}</span>
                    )}
                    {item.customerProductSku && item.productSku && ' | '}
                    {item.productSku && <span>SKU: {item.productSku}</span>}
                  </span>
                )}
              </div>
            </TableCell>
            <TableCell className="text-right">{item.quantity ?? '-'}</TableCell>
            <TableCell className="text-right">
              {formatCurrencyValue(item.unitPrice, currency ?? 'USD')}
            </TableCell>
            <TableCell className="text-right font-medium">
              {formatCurrencyValue(item.lineTotal, currency ?? 'USD')}
            </TableCell>
            <TableCell>
              {item.rescheduledDate
                ? formatDateOnlyString(item.rescheduledDate)
                : '-'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
